import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth/auth-context';
import { StatCard } from '../components/StatCard';
import { Tag } from '../components/Tag';
import { Carregando, ErroBox, PageHeader, Vazio } from '../components/ui';
import { CORES_STATUS } from './Eventos';

interface Resumo {
  contadores: {
    eventosEmOperacao: number;
    eventosTotal: number;
    participantes: number;
    pessoas: number;
    usuarios: number;
    templatesAprovados: number;
    importacoes: number;
    jobsPendentes: number;
  };
  etapas: { status: string; total: number }[];
  proximosEventos: {
    id: string;
    name: string;
    status: string;
    format: string;
    city: string | null;
    startDate: string | null;
    endDate: string | null;
    participantes: number;
  }[];
}

const ROTULO_ETAPA: Record<string, string> = {
  RASCUNHO: 'Rascunho',
  EM_CONFIGURACAO: 'Em configuração',
  PRONTO: 'Pronto',
  ATIVO: 'Ativo',
  PAUSADO: 'Pausado',
  ENCERRADO: 'Encerrado',
};

function saudacao() {
  const h = new Date().getHours();
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
}

const HOJE_POR_EXTENSO = () =>
  new Date().toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });

function dataCurta(iso: string | null) {
  if (!iso) return 'sem data';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

export function Dashboard() {
  const { usuario } = useAuth();

  const { data, isLoading, error } = useQuery<Resumo>({
    queryKey: ['dashboard'],
    queryFn: () => api<Resumo>('/dashboard'),
    refetchInterval: 60_000,
  });

  const primeiroNome = usuario?.name?.split(' ')[0] ?? '';

  if (isLoading) return <Carregando />;
  if (error) return <ErroBox erro={error} />;
  if (!data) return null;

  const { contadores, etapas, proximosEventos } = data;

  // A barra é proporcional à maior etapa, não ao total: com 40 rascunhos e 1
  // ativo, proporção sobre o total deixaria o ativo invisível — e é justamente
  // o que a pessoa quer enxergar.
  const maiorEtapa = Math.max(1, ...etapas.map((e) => e.total));

  return (
    <>
      <PageHeader
        titulo={`${saudacao()}${primeiroNome ? `, ${primeiroNome}` : ''} 👋`}
        subtitulo={`Aqui está o resumo — ${HOJE_POR_EXTENSO()}`}
      />

      <div className="stats-grid">
        <StatCard
          label="Eventos em operação"
          value={contadores.eventosEmOperacao}
          icon="ti-calendar-event"
          deltaLabel={`${contadores.eventosTotal} no total`}
        />
        <StatCard
          label="Participantes"
          value={contadores.participantes}
          icon="ti-users"
          iconBg="rgba(96,165,250,0.12)"
          iconColor="var(--info)"
          deltaLabel={`${contadores.pessoas} pessoa(s) distinta(s)`}
        />
        <StatCard
          label="Importações"
          value={contadores.importacoes}
          icon="ti-file-spreadsheet"
          iconBg="var(--success-dim)"
          iconColor="var(--success)"
          deltaLabel="planilhas processadas"
        />
        <StatCard
          label="Templates aprovados"
          value={contadores.templatesAprovados}
          icon="ti-message-2-check"
          iconBg="rgba(37,211,102,0.12)"
          iconColor="#25D366"
          deltaLabel="prontos para envio"
        />
        <StatCard
          label="Usuários"
          value={contadores.usuarios}
          icon="ti-user-shield"
          deltaLabel="com acesso"
        />
        <StatCard
          label="Fila"
          value={contadores.jobsPendentes}
          icon="ti-stack-2"
          iconBg={
            contadores.jobsPendentes > 50 ? 'rgba(251,191,36,0.12)' : 'rgba(96,165,250,0.12)'
          }
          iconColor={contadores.jobsPendentes > 50 ? 'var(--warning)' : 'var(--info)'}
          deltaLabel="jobs pendentes"
        />
      </div>

      <div className="grade-detalhe">
        <div className="card">
          <div className="secao-titulo">
            <i className="ti ti-chart-bar" />
            Eventos por etapa
          </div>

          {contadores.eventosTotal === 0 ? (
            <Vazio
              icone="ti-calendar-plus"
              titulo="Nenhum evento ainda"
              descricao="Crie o primeiro evento para começar."
            />
          ) : (
            <div className="etapas">
              {etapas.map((etapa) => (
                <div className="etapa-linha" key={etapa.status}>
                  <div className="etapa-rotulo">{ROTULO_ETAPA[etapa.status] ?? etapa.status}</div>
                  <div className="etapa-trilho">
                    <div
                      className={`etapa-barra etapa-${CORES_STATUS[etapa.status] ?? 'muted'}`}
                      style={{ width: `${(etapa.total / maiorEtapa) * 100}%` }}
                    />
                  </div>
                  <div className="etapa-total">{etapa.total}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <div className="secao-titulo" style={{ justifyContent: 'space-between' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <i className="ti ti-calendar" />
              Próximos eventos
            </span>
            <Link to="/eventos" className="btn btn-ghost btn-sm">
              Ver todos →
            </Link>
          </div>

          {proximosEventos.length === 0 ? (
            <Vazio icone="ti-calendar" titulo="Nada agendado" descricao="Sem eventos cadastrados." />
          ) : (
            <div className="lista-eventos">
              {proximosEventos.map((evento) => (
                <Link to={`/eventos/${evento.id}`} className="evento-linha" key={evento.id}>
                  <div>
                    <div className="evento-nome">{evento.name}</div>
                    <div className="evento-meta">
                      {dataCurta(evento.startDate)}
                      {evento.city ? ` · ${evento.city}` : ''}
                    </div>
                  </div>
                  <div className="evento-direita">
                    <Tag type={CORES_STATUS[evento.status] ?? 'muted'}>
                      {ROTULO_ETAPA[evento.status] ?? evento.status}
                    </Tag>
                    <span className="evento-participantes" title="participantes">
                      <i className="ti ti-users" style={{ fontSize: 12 }} /> {evento.participantes}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
